<template>
  <!-- 回复邮件 - 浮窗模式 -->
  <Teleport to="body">
    <!-- 最小化状态 -->
    <div 
      v-if="showModal && isMinimized"
      class="fixed bottom-0 right-4 z-[60] bg-blue-600 text-white rounded-t-lg shadow-lg cursor-pointer flex items-center px-4 py-2 space-x-3 hover:bg-blue-700 transition-colors"
      style="width: 320px;"
      @click="isMinimized = false"
    >
      <span class="flex-1 text-sm font-medium truncate">{{ isReply ? '回复' : '撰写' }}: {{ replyForm.subject || '新邮件' }}</span>
      <button @click.stop="isMinimized = false" class="hover:bg-blue-500 rounded p-0.5" title="展开">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
      </button>
      <button @click.stop="closeModal" class="hover:bg-blue-500 rounded p-0.5" title="关闭">
        <XMarkIcon class="h-4 w-4" />
      </button>
    </div>

    <!-- 展开状态 -->
    <div 
      v-if="showModal && !isMinimized"
      class="fixed z-[60] bg-white rounded-t-lg shadow-2xl border border-gray-300 flex flex-col"
      :style="windowStyle"
    >
      <!-- 标题栏 - 可拖拽 -->
      <div 
        class="flex items-center justify-between px-4 py-2 bg-gray-800 text-white rounded-t-lg cursor-move flex-shrink-0 select-none"
        @mousedown="startDrag"
      >
        <div class="flex-1 min-w-0 mr-3">
          <span class="text-sm font-medium truncate block">
            {{ isEditMode ? '编辑定时邮件' : (isResendMode ? '重发邮件' : (isReply ? '回复邮件' : '撰写邮件')) }}
            <span v-if="originalEmail && !isEditMode" class="text-gray-300 font-normal"> - {{ originalEmail.subject }}</span>
          </span>
        </div>
        <div class="flex items-center space-x-1 flex-shrink-0">
          <button @click.stop="isMinimized = true" class="hover:bg-gray-600 rounded p-1" title="最小化">
            <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" /></svg>
          </button>
          <button @click.stop="toggleMaximize" class="hover:bg-gray-600 rounded p-1" :title="isMaximized ? '还原' : '最大化'">
            <svg v-if="!isMaximized" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
            <svg v-else class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" /></svg>
          </button>
          <button @click.stop="closeModal" class="hover:bg-red-500 rounded p-1" title="关闭">
            <XMarkIcon class="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <!-- 主体内容 -->
      <div class="flex-1 flex flex-col min-h-0 overflow-hidden p-4">

        <!-- 内容区域 -->
        <div class="flex-1 flex flex-col min-h-0">
          <!-- Tab 导航 -->
          <div class="border-b border-gray-200 mb-3 flex-shrink-0">
            <nav class="-mb-px flex space-x-8">
              <button
                @click="activeTab = 'compose'"
                :class="[
                  activeTab === 'compose'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors'
                ]"
              >
                撰写邮件
              </button>
              <button
                @click="activeTab = 'options'"
                :class="[
                  activeTab === 'options'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center'
                ]"
              >
                <span>抄送和附件</span>
                <span v-if="replyForm.ccEmails.trim() || replyForm.attachments.length > 0" class="ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ (replyForm.ccEmails.trim() ? 1 : 0) + replyForm.attachments.length }}
                </span>
              </button>
            </nav>
          </div>

          <!-- Tab 内容 -->
          <div class="flex-1 flex flex-col min-h-0">
            <!-- 撰写邮件 Tab -->
            <div v-show="activeTab === 'compose'" class="flex-1 flex flex-col min-h-0">
              <!-- 表单字段区域 -->
              <div class="flex-shrink-0 space-y-4 mb-4">
              <!-- 发件账户、模板选择和收件人 -->
              <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    发件账户 <span class="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    v-model="replyForm.fromAccountId"
                    :options="accountOptions"
                    placeholder="请选择发件账户"
                    label-key="name"
                    value-key="id"
                    :disabled="loadingSendAccounts"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    邮件模板 (可选)
                  </label>
                  <SearchableSelect
                    v-model="selectedTemplateId"
                    :options="templateOptions"
                    placeholder="选择模板（可选）"
                    label-key="name"
                    value-key="id"
                    :disabled="loadingTemplates"
                    @change="onTemplateChange"
                  />
                </div>

                <!-- 收件人 -->
                <div class="col-span-2 lg:col-span-1">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    收件人 <span class="text-red-500">*</span>
                  </label>
                  
                  <!-- 新建邮件：可编辑的收件人输入框 -->
                  <input
                    v-if="!isReply"
                    v-model="manualRecipientInput"
                    type="text"
                    placeholder="请输入收件人邮箱地址，多个邮箱用逗号或空格分隔"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    @input="updateRecipientsFromInput"
                  />
                  
                  <!-- 回复邮件：只读的收件人显示 -->
                  <div v-else class="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700 truncate" :title="getRecipientsDisplay()">
                    {{ getRecipientsDisplay() }}
                  </div>
                </div>
              </div>

              <!-- 邮件主题 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  邮件主题 <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="replyForm.subject"
                  type="text"
                  placeholder="请输入邮件主题"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              </div>

              <!-- 邮件内容 - 自适应高度 -->
              <div class="flex-1 flex flex-col min-h-0" style="min-height: 300px;">
                <label class="block text-sm font-medium text-gray-700 mb-2 flex-shrink-0">
                  邮件内容 <span class="text-red-500">*</span>
                </label>
                <div class="flex-1 border-gray-300 rounded-md min-h-0">
                  <!-- 模板加载中的骨架效果 -->
                  <div v-if="loadingTemplate || ((isEditMode || isResendMode) && loadingEditData)" class="relative h-full flex flex-col animate-pulse bg-white rounded-md border border-gray-300">
                    <!-- 工具栏骨架 -->
                    <div class="border-b border-gray-200 p-2 space-y-2">
                      <div class="flex space-x-2">
                        <div class="h-8 w-8 bg-gray-200 rounded"></div>
                        <div class="h-8 w-8 bg-gray-200 rounded"></div>
                        <div class="h-8 w-8 bg-gray-200 rounded"></div>
                        <div class="h-8 w-16 bg-gray-200 rounded"></div>
                        <div class="h-8 w-16 bg-gray-200 rounded"></div>
                        <div class="h-8 w-8 bg-gray-200 rounded"></div>
                        <div class="h-8 w-8 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                    <!-- 内容区域骨架 -->
                    <div class="flex-1 p-4 space-y-3">
                      <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div class="h-4 bg-gray-200 rounded w-full"></div>
                      <div class="h-4 bg-gray-200 rounded w-5/6"></div>
                      <div class="h-4 bg-gray-200 rounded w-2/3"></div>
                      <div class="h-4 bg-gray-200 rounded w-4/5"></div>
                      <div class="h-4 bg-gray-200 rounded w-3/5"></div>
                      <div class="h-4 bg-gray-200 rounded w-full"></div>
                      <div class="h-4 bg-gray-200 rounded w-2/5"></div>
                    </div>
                    <!-- 加载提示 -->
                    <div class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                      <div class="flex items-center space-x-2 text-gray-600">
                        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="text-sm">{{ (isEditMode || isResendMode) && loadingEditData ? '正在加载邮件内容...' : '正在加载模板内容...' }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- TinyMCE 富文本编辑器 -->
                  <TinyMCEEditor
                    v-else
                    :key="editorMountKey"
                    ref="editorInstance"
                    v-model="replyForm.content"
                    height="100%"
                    placeholder="请输入邮件内容..."
                  />
                </div>
              </div>
            </div>

            <!-- 抄送和附件 Tab -->
            <div v-show="activeTab === 'options'" class="flex-1 overflow-y-auto">
              <div class="space-y-4">
              <!-- 抄送 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  抄送 (可选)
                </label>
                <input
                  v-model="replyForm.ccEmails"
                  type="text"
                  placeholder="多个邮箱用逗号或空格分隔，如：user1@example.com user2@example.com"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <!-- 附件上传 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-3">附件 (可选)</label>
                
                <!-- 文件上传区域 -->
                <div 
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                  :class="{ 'border-blue-400 bg-blue-50': isDragging }"
                  @dragover.prevent
                  @dragenter.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="handleFileDrop"
                >
                  <DocumentIcon class="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <div class="text-sm text-gray-600">
                    <label class="cursor-pointer text-blue-600 hover:text-blue-500 font-medium">
                      选择文件
                      <input 
                        type="file" 
                        multiple
                        accept="*/*"
                        @change="handleFileUpload" 
                        class="sr-only" 
                      />
                    </label>
                    或拖拽文件到此处
                  </div>
                  <p class="text-xs text-gray-500 mt-1">支持所有文件格式，单文件大小限制 25MB</p>
                </div>

                <!-- 已有附件（编辑定时邮件） -->
                <div v-if="existingScheduledAttachments.length > 0" class="mt-3 space-y-2">
                  <div
                    v-for="att in existingScheduledAttachments"
                    :key="att.id"
                    class="bg-blue-50 border border-blue-100 rounded-lg p-3"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3">
                          <DocumentIcon class="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div class="text-sm font-medium text-gray-900">{{ att.filename }}</div>
                          <div class="text-xs text-gray-500">{{ formatFileSize(att.size) }} · 已保存</div>
                        </div>
                      </div>
                      <button
                        @click="removeExistingAttachment(att.id)"
                        class="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 新上传附件列表 -->
                <div v-if="replyForm.attachments.length > 0" class="mt-3 space-y-2">
                  <div
                    v-for="(file, index) in replyForm.attachments"
                    :key="index"
                    class="bg-gray-50 border border-gray-200 rounded-lg p-3"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3">
                          <DocumentIcon class="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div class="text-sm font-medium text-gray-900">{{ file.name }}</div>
                          <div class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</div>
                        </div>
                      </div>
                      <button
                        @click="removeAttachment(index)"
                        class="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="border-t border-gray-200 pt-3 bg-white flex-shrink-0">
          <!-- 发送方式选择 -->
          <div class="flex items-center justify-between mb-3 px-1">
            <div v-if="isEditMode" class="flex items-center space-x-2 w-full">
              <span class="text-sm text-gray-600 whitespace-nowrap">计划发送时间</span>
              <input
                v-model="scheduledAt"
                type="datetime-local"
                :min="minScheduledTime"
                :max="maxScheduledTime"
                class="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <template v-else>
            <div class="flex items-center space-x-4">
              <label class="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  v-model="sendMode"
                  value="immediate"
                  class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span class="ml-1.5 text-sm text-gray-700">立即发送</span>
              </label>
              <label class="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  v-model="sendMode"
                  value="scheduled"
                  class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span class="ml-1.5 text-sm text-gray-700">定时发送</span>
              </label>
            </div>
            <div v-if="sendMode === 'scheduled'" class="flex items-center space-x-2">
              <label class="text-sm text-gray-600 whitespace-nowrap">发送时间</label>
              <input
                v-model="scheduledAt"
                type="datetime-local"
                :min="minScheduledTime"
                :max="maxScheduledTime"
                class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            </template>
          </div>
          <div class="flex items-center justify-end space-x-3">
            <button
              @click="closeModal"
              class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              @click="sendEmail(false)"
              :disabled="sending || checkingSensitiveWords || !isFormValid || loadingEditData"
              class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg v-if="sending || checkingSensitiveWords" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 0 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <PaperAirplaneIcon v-else class="h-4 w-4 mr-2" />
              {{ sendButtonLabel }}
            </button>
          </div>
        </div>
      </div>

      <!-- 右下角拖拽调整大小手柄 -->
      <div 
        v-if="!isMaximized"
        class="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize"
        @mousedown.stop="startResize($event, 'sw')"
      ></div>
    </div>
  </Teleport>

  <!-- 敏感词警告模态框 -->
  <SensitiveWordWarningModal
    :show="showSensitiveWordWarning"
    :sensitive-word-data="sensitiveWordData"
    mode="send"
    @confirm="handleSensitiveWordWarningConfirm"
    @close="handleSensitiveWordWarningClose"
  />

  <!-- 敏感词错误模态框 -->
  <SensitiveWordErrorModal
    :show="showSensitiveWordError"
    :sensitive-word-data="sensitiveWordErrorData"
    mode="send"
    @close="handleSensitiveWordErrorClose"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { 
  XMarkIcon, 
  PaperAirplaneIcon, 
  PaperClipIcon,
  DocumentIcon
} from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import emailTemplateService from '@/services/emailTemplateService'
import emailServiceV2 from '@/services/emailServiceV2'
import TinyMCEEditor from '@/components/common/TinyMCEEditor.vue'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import SensitiveWordWarningModal from '@/components/templates/SensitiveWordWarningModal.vue'
import SensitiveWordErrorModal from '@/components/templates/SensitiveWordErrorModal.vue'

// Props
const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  },
  originalEmail: {
    type: Object,
    default: null
  },
  isReply: {
    type: Boolean,
    default: true
  },
  targetCustomer: {
    type: Object,
    default: null
  },
  defaultFromAccountId: {
    type: [String, Number],
    default: null
  },
  bulkRecipients: {
    type: String,
    default: ''
  },
  editReplyLogId: {
    type: [Number, String],
    default: null
  },
  resendReplyLogId: {
    type: [Number, String],
    default: null
  }
})

// Emits
const emit = defineEmits(['close', 'sent', 'updated'])

// 响应式数据
const toast = useToast()
const sending = ref(false)
const loadingSendAccounts = ref(false)
const loadingTemplates = ref(false)
const availableAccounts = ref([])
const templates = ref([])
const selectedTemplateId = ref('')
const editorInstance = ref(null)
const activeTab = ref('compose')
const isDragging = ref(false)
const loadingTemplate = ref(false)  // 模板加载状态
const manualRecipientInput = ref('')  // 手动输入的收件人
const sendMode = ref('immediate')  // immediate | scheduled
const scheduledAt = ref('')
const existingScheduledAttachments = ref([])
const loadingEditData = ref(false)
const editorMountKey = ref(0)

const isEditMode = computed(() => !!props.editReplyLogId)
const isResendMode = computed(() => !!props.resendReplyLogId)

// 定时发送时间范围
const minScheduledTime = computed(() => {
  const d = new Date(Date.now() + 5 * 60 * 1000)
  return formatDatetimeLocal(d)
})
const maxScheduledTime = computed(() => {
  const d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  return formatDatetimeLocal(d)
})
const sendButtonLabel = computed(() => {
  if (isEditMode.value) {
    if (checkingSensitiveWords.value) return '保存中...'
    if (sending.value) return '保存中...'
    return '保存修改'
  }
  if (checkingSensitiveWords.value) return sendMode.value === 'scheduled' ? '计划中...' : '发送邮件'
  if (sending.value) return sendMode.value === 'scheduled' ? '计划中...' : '发送中...'
  return sendMode.value === 'scheduled' ? '定时发送' : '发送邮件'
})

const formatDatetimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const toISOString = (datetimeLocal) => {
  if (!datetimeLocal) return null
  return new Date(datetimeLocal).toISOString()
}

// 浮窗状态
const isMinimized = ref(false)
const isMaximized = ref(false)
const windowPos = ref({ x: 0, y: 0 })
const windowSize = ref({ w: 680, h: 560 })
const dragState = ref(null)
const resizeState = ref(null)
const savedWindowState = ref(null) // 最大化前的状态

// 窗口样式计算
const windowStyle = computed(() => {
  if (isMaximized.value) {
    return { left: '0px', top: '0px', width: '100vw', height: '100vh', borderRadius: '0' }
  }
  return {
    left: windowPos.value.x + 'px',
    top: windowPos.value.y + 'px',
    width: windowSize.value.w + 'px',
    height: windowSize.value.h + 'px'
  }
})

// 初始化窗口位置（居中显示）
const initWindowPosition = () => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  windowSize.value = { w: Math.min(900, vw - 60), h: Math.min(680, vh - 60) }
  windowPos.value = {
    x: Math.max(0, (vw - windowSize.value.w) / 2),
    y: Math.max(0, (vh - windowSize.value.h) / 2)
  }
}

// 切换最大化
const toggleMaximize = () => {
  if (isMaximized.value) {
    if (savedWindowState.value) {
      windowPos.value = { ...savedWindowState.value.pos }
      windowSize.value = { ...savedWindowState.value.size }
    }
    isMaximized.value = false
  } else {
    savedWindowState.value = {
      pos: { ...windowPos.value },
      size: { ...windowSize.value }
    }
    isMaximized.value = true
  }
}

// 拖拽逻辑
const startDrag = (e) => {
  if (isMaximized.value) return
  dragState.value = { startX: e.clientX - windowPos.value.x, startY: e.clientY - windowPos.value.y }
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}
const onDrag = (e) => {
  if (!dragState.value) return
  const x = Math.max(0, Math.min(e.clientX - dragState.value.startX, window.innerWidth - 100))
  const y = Math.max(0, Math.min(e.clientY - dragState.value.startY, window.innerHeight - 50))
  windowPos.value = { x, y }
}
const stopDrag = () => {
  dragState.value = null
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

// 调整大小逻辑
const startResize = (e, direction) => {
  resizeState.value = { startX: e.clientX, startY: e.clientY, startW: windowSize.value.w, startH: windowSize.value.h, startPosX: windowPos.value.x, dir: direction }
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}
const onResize = (e) => {
  if (!resizeState.value) return
  const s = resizeState.value
  if (s.dir === 'sw') {
    const dx = s.startX - e.clientX
    const dy = e.clientY - s.startY
    const newW = Math.max(480, s.startW + dx)
    const newH = Math.max(400, s.startH + dy)
    windowPos.value.x = s.startPosX - (newW - s.startW)
    windowSize.value = { w: newW, h: newH }
  }
}
const stopResize = () => {
  resizeState.value = null
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})

// 敏感词检测相关状态
const showSensitiveWordWarning = ref(false)
const showSensitiveWordError = ref(false)
const sensitiveWordData = ref({})
const sensitiveWordErrorData = ref({})
const checkingSensitiveWords = ref(false) // 敏感词检测loading状态

// 保存初始邮件内容，用于模板还原
const initialEmailData = ref({
  subject: '',
  content: ''
})

// 回复表单数据
const replyForm = ref({
  fromAccountId: '',
  recipients: [{ email: '', name: '' }],
  ccEmails: '',
  subject: '',
  content: '',
  attachments: []
})

// 计算属性
const isFormValid = computed(() => {
  return replyForm.value.fromAccountId &&
         replyForm.value.recipients.some(r => r.email.trim()) &&
         replyForm.value.subject.trim() &&
         replyForm.value.content.trim()
})

// 为 SearchableSelect 准备发件账户选项
const accountOptions = computed(() => {
  return [
    { id: '', name: '请选择发件账户', email: '' }, // 空选项
    ...availableAccounts.value.map(account => ({
      id: account.id,
      name: `${account.display_name} (${account.email})`,
      email: account.email
    }))
  ]
})

// 为 SearchableSelect 准备邮件模板选项
const templateOptions = computed(() => {
  return [
    { id: '', name: '选择模板（可选）' }, // 空选项
    ...templates.value.map(template => ({
      id: template.id,
      name: template.name
    }))
  ]
})

// 监听原邮件变化，自动填充表单
watch(() => props.originalEmail, (newEmail) => {
  if (newEmail && props.isReply) {
    fillReplyForm(newEmail)
  } else if (!props.isReply) {
    // 新邮件（非回复）的情况下，设置初始状态为空
    initialEmailData.value = {
      subject: '',
      content: ''
    }
    // 清空收件人和手动输入
    replyForm.value.recipients = []
    manualRecipientInput.value = ''
  }
}, { immediate: true })

// 监听目标客户变化，自动填充收件人
watch(() => props.targetCustomer, (newCustomer) => {
  if (newCustomer && !props.isReply) {
    fillCustomerForm(newCustomer)
  }
}, { immediate: true })

// 监听群发收件人变化，自动填充收件人
watch(() => props.bulkRecipients, (newBulkRecipients) => {
  if (newBulkRecipients && !props.isReply && !props.targetCustomer) {
    // 🔧 群发模式：解析客户对象数组或邮箱字符串
    try {
      // 尝试解析为客户对象数组（新格式）
      const customerObjects = JSON.parse(newBulkRecipients)
      
      if (Array.isArray(customerObjects) && customerObjects.length > 0) {
        // 新格式：客户对象数组 [{id, email, name}, ...]
        replyForm.value.recipients = customerObjects.map(customer => ({
          email: customer.email || '',
          name: customer.name || '',
          customerId: customer.id || null // 🔧 保留客户ID
        }))
        
        // 更新手动输入字段显示
        manualRecipientInput.value = customerObjects
          .map(c => c.name ? `${c.name} <${c.email}>` : c.email)
          .join(', ')
      } else {
        // 旧格式：邮箱字符串（兼容性）
        manualRecipientInput.value = newBulkRecipients
        updateRecipientsFromInput()
      }
    } catch (e) {
      // 不是JSON格式，按旧格式处理（邮箱字符串）
      manualRecipientInput.value = newBulkRecipients
      updateRecipientsFromInput()
    }
  }
}, { immediate: true })

// 监听模态框显示状态
watch(() => props.showModal, async (show) => {
  if (show) {
    isMinimized.value = false
    isMaximized.value = false
    initWindowPosition()
    loadAvailableAccounts()
    loadTemplates()
    if (props.editReplyLogId) {
      await loadScheduledReplyForEdit(props.editReplyLogId)
    } else if (props.resendReplyLogId) {
      await loadFailedReplyForResend(props.resendReplyLogId)
    }
  }
})

// 切换到定时发送时，默认设置为1小时后
watch(sendMode, (mode) => {
  if (mode === 'scheduled' && !scheduledAt.value) {
    const d = new Date(Date.now() + 60 * 60 * 1000)
    scheduledAt.value = formatDatetimeLocal(d)
  }
})

// 方法
const closeModal = () => {
  emit('close')
  resetForm()
}

const resetForm = () => {
  replyForm.value = {
    fromAccountId: '',
    recipients: [{ email: '', name: '' }],
    ccEmails: '',
    subject: '',
    content: '',
    attachments: []
  }
  selectedTemplateId.value = ''
  activeTab.value = 'compose'
  isDragging.value = false
  manualRecipientInput.value = ''  // 清空手动输入的收件人
  sendMode.value = 'immediate'
  scheduledAt.value = ''
  existingScheduledAttachments.value = []
  editorMountKey.value = 0
  
  // 重置初始邮件数据
  initialEmailData.value = {
    subject: '',
    content: ''
  }
  
  // 清理敏感词相关状态
  showSensitiveWordWarning.value = false
  showSensitiveWordError.value = false
  sensitiveWordData.value = {}
  sensitiveWordErrorData.value = {}
  checkingSensitiveWords.value = false
}

const removeExistingAttachment = (attachmentId) => {
  existingScheduledAttachments.value = existingScheduledAttachments.value.filter(
    att => att.id !== attachmentId
  )
}

const loadScheduledReplyForEdit = async (logId) => {
  try {
    loadingEditData.value = true
    const log = await emailServiceV2.getReplyLog(logId)
    if (!log || log.status !== 'scheduled') {
      toast.error('该任务不可编辑')
      closeModal()
      return
    }

    sendMode.value = 'scheduled'
    scheduledAt.value = log.scheduled_at ? formatDatetimeLocal(new Date(log.scheduled_at)) : ''
    replyForm.value.fromAccountId = log.send_account
    replyForm.value.subject = log.subject || ''
    replyForm.value.content = log.body_html || ''
    replyForm.value.recipients = (log.to_emails || []).map(email => ({ email, name: email }))
    replyForm.value.ccEmails = (log.cc_emails || []).join(', ')
    replyForm.value.attachments = []
    existingScheduledAttachments.value = log.attachments || []
    manualRecipientInput.value = (log.to_emails || []).join(', ')

    initialEmailData.value = {
      subject: replyForm.value.subject,
      content: replyForm.value.content
    }
    editorMountKey.value += 1
  } catch (error) {
    console.error('加载定时邮件失败:', error)
    toast.error('加载定时邮件失败')
    closeModal()
  } finally {
    loadingEditData.value = false
  }
}

const loadFailedReplyForResend = async (logId) => {
  try {
    loadingEditData.value = true
    const log = await emailServiceV2.getReplyLog(logId)
    if (!log || log.status !== 'failed') {
      toast.error('该任务不可重发')
      closeModal()
      return
    }

    sendMode.value = 'immediate'
    scheduledAt.value = ''
    replyForm.value.fromAccountId = log.send_account
    replyForm.value.subject = log.subject || ''
    replyForm.value.content = log.body_html || ''
    replyForm.value.recipients = (log.to_emails || []).map(email => ({ email, name: email }))
    replyForm.value.ccEmails = (log.cc_emails || []).join(', ')
    replyForm.value.attachments = []
    existingScheduledAttachments.value = []
    manualRecipientInput.value = (log.to_emails || []).join(', ')

    initialEmailData.value = {
      subject: replyForm.value.subject,
      content: replyForm.value.content
    }
    editorMountKey.value += 1
  } catch (error) {
    console.error('加载失败邮件失败:', error)
    toast.error('加载失败邮件失败')
    closeModal()
  } finally {
    loadingEditData.value = false
  }
}

// 填充回复表单
const fillReplyForm = (email) => {
  // 根据文件夹类型确定回复对象
  const folderType = email.folder_type || 'inbox'
  
  if (folderType === 'sent') {
    // 已发送邮件：回复给收件人
    if (email.recipient_emails && email.recipient_emails.length > 0) {
      replyForm.value.recipients = email.recipient_emails.map(recipientEmail => ({
        email: recipientEmail,
        name: email.recipient_names && email.recipient_names[recipientEmail] || ''
      }))
    }
  } else {
    // 收件箱等：回复给发件人
    replyForm.value.recipients = [{
      email: email.sender_email || '',
      name: email.sender_name || ''
    }]
  }
  
  // 设置回复主题
  const originalSubject = email.subject || ''
  replyForm.value.subject = originalSubject.startsWith('Re: ') 
    ? originalSubject 
    : `Re: ${originalSubject}`
  
  // 设置回复内容（引用原邮件）
  const quotedContent = generateQuotedContent(email)
  replyForm.value.content = quotedContent

  // 保存初始邮件数据，用于模板还原
  initialEmailData.value = {
    subject: replyForm.value.subject,
    content: replyForm.value.content
  }
}

// 填充客户表单
const fillCustomerForm = (customer) => {
  // 🔧 前端继续显示脱敏邮箱地址，后端自动解析
  // 同时传递相关ID给后端，用于邮箱地址解析
  const recipient = {
    email: customer.email || '',
    customerId: customer.id, // 🔧 添加客户ID，后端用于解析真实邮箱
    name: customer.first_name && customer.last_name 
      ? `${customer.first_name} ${customer.last_name}` 
      : (customer.first_name || customer.last_name || '')
  }
  
  // 🔧 如果有品牌独立站联系表单ID，添加到收件人信息中
  if (customer.contact_id) {
    recipient.contact_id = customer.contact_id
  }
  
  // 🔧 如果有二维码表单提交ID，添加到收件人信息中
  if (customer.submission_id) {
    recipient.submission_id = customer.submission_id
  }
  
  replyForm.value.recipients = [recipient]
  
  // 同步更新手动输入字段
  manualRecipientInput.value = customer.email || ''
  
  // 清空主题和内容，让用户自由填写
  replyForm.value.subject = ''
  replyForm.value.content = ''
  
  // 保存初始邮件数据，用于模板还原
  initialEmailData.value = {
    subject: '',
    content: ''
  }
}

// 生成引用原邮件内容
const generateQuotedContent = (email) => {
  const date = new Date(email.received_date).toLocaleString('zh-CN')
  const sender = email.sender_name ? `${email.sender_name} &lt;${email.sender_email}&gt;` : email.sender_email
  
  // 使用HTML格式的邮件内容，如果没有则使用文本内容并转换为HTML
  let originalContent = ''
  if (email.body_html) {
    // 如果有HTML内容，直接使用
    originalContent = email.body_html
  } else if (email.body_text) {
    // 如果只有文本内容，转换为HTML格式
    originalContent = email.body_text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>')
  }
  
  return `<br><br><div style="border-left: 4px solid #e5e7eb; padding-left: 16px; margin: 16px 0; color: #6b7280;">
    <p><strong>------- Original Message -------</strong></p>
    <p><strong>From:</strong> ${sender}</p>
    <p><strong>Sent:</strong> ${date}</p>
    <p><strong>Subject:</strong> ${email.subject || 'no subject'}</p>
    <br>
    <div>${originalContent}</div>
  </div>`
}

// 获取收件人显示文本
const getRecipientsDisplay = () => {
  if (!replyForm.value.recipients || replyForm.value.recipients.length === 0) {
    return '无收件人'
  }
  
  return replyForm.value.recipients
    .filter(r => r.email.trim())
    .map(r => r.name ? `${r.name} <${r.email}>` : r.email)
    .join(', ')
}

// 从手动输入更新收件人列表
const updateRecipientsFromInput = () => {
  if (!manualRecipientInput.value.trim()) {
    replyForm.value.recipients = []
    return
  }

  // 解析逗号或空格分隔的邮箱地址
  const emailList = manualRecipientInput.value
    .split(/[,\s]+/)
    .map(email => email.trim())
    .filter(email => email.length > 0)
  
  // 转换为收件人对象数组
  replyForm.value.recipients = emailList.map(email => ({
    email: email,
    name: '', // 手动输入时没有姓名
    customerId: null
  }))
}

// 模板选择处理 - 适配 SearchableSelect 组件的 change 事件
const onTemplateChange = async (selectedOption) => {
  // SearchableSelect 的 change 事件会传递选中的选项对象
  const templateId = selectedOption ? selectedOption.id : selectedTemplateId.value
  
  if (!templateId) {
    // 重置加载状态
    loadingTemplate.value = false
    // 恢复初始邮件内容和主题
    if (initialEmailData.value.subject || initialEmailData.value.content) {
      replyForm.value.subject = initialEmailData.value.subject
      replyForm.value.content = initialEmailData.value.content
    }
    return
  }

  const selectedTemplate = templates.value.find(t => t.id == templateId)
  if (!selectedTemplate) {
    loadingTemplate.value = false
    toast.error('模板未找到')
    return
  }

  try {
    // 设置加载状态
    loadingTemplate.value = true
    
    // 获取模板完整内容
    const result = await emailTemplateService.getTemplate(templateId)
    if (!result.success) {
      toast.error(result.message || '获取模板内容失败')
      loadingTemplate.value = false
      return
    }

    const template = result.data

    // 填充主题
    if (template.subject && template.subject.trim()) {
      replyForm.value.subject = template.subject.trim()
    }
    
    // 填充内容
    let templateContent = ''
    if (template.body_html && template.body_html.trim()) {
      templateContent = template.body_html.trim()
    } else if (template.body_text && template.body_text.trim()) {
      // 如果没有HTML内容，使用纯文本并转换为HTML
      templateContent = template.body_text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    }

    if (templateContent) {
      // 直接使用模板内容，清空原有内容
      replyForm.value.content = templateContent
    } else {
      toast.warning('模板内容为空')
    }
    
    // 聚焦到富文本编辑器，让用户可以立即编辑
    nextTick(() => {
      try {
        if (editorInstance.value && typeof editorInstance.value.focus === 'function') {
          editorInstance.value.focus()
        }
      } catch (error) {
        // 如果聚焦失败，忽略错误（不影响功能）
        console.log('无法聚焦到编辑器:', error)
      }
    })
    
  } catch (error) {
    console.error('应用模板时出错:', error)
    toast.error('应用模板失败，请重试')
  } finally {
    loadingTemplate.value = false
  }
}

// 附件管理
const removeAttachment = (index) => {
  replyForm.value.attachments.splice(index, 1)
  toast.success('附件已移除')
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 加载可用发件账户
const loadAvailableAccounts = async () => {
  try {
    loadingSendAccounts.value = true
    const result = await emailServiceV2.getEmailAccounts({ page_size: 10000 })
    if (result.success) {
      availableAccounts.value = result.results.filter(account => account.is_active)
      
      // 优先级：1.默认账户 2.回复相关账户 3.不自动选择（让用户手动选择）
      if (props.defaultFromAccountId) {
        // 如果有指定的默认发件账户，优先选择该账户
        const defaultAccount = availableAccounts.value.find(account => 
          account.id == props.defaultFromAccountId
        )
        if (defaultAccount) {
          replyForm.value.fromAccountId = defaultAccount.id
        }
      } else if (props.originalEmail && props.isReply) {
        // 如果是回复，尝试选择与原邮件相关的账户
        const relatedAccount = availableAccounts.value.find(account => 
          account.email === props.originalEmail.account_email
        )
        if (relatedAccount) {
          replyForm.value.fromAccountId = relatedAccount.id
        } else if (availableAccounts.value.length > 0) {
          replyForm.value.fromAccountId = availableAccounts.value[0].id
        }
      }
      // 对于通用新建邮件（聚合管理），不自动选择任何账户，让用户手动选择
    }
  } catch (error) {
    console.error('加载发件账户失败:', error)
    toast.error('加载发件账户失败')
  } finally {
    loadingSendAccounts.value = false
  }
}

// 加载邮件模板
const loadTemplates = async () => {
  try {
    loadingTemplates.value = true
    const result = await emailTemplateService.getTemplates({ page_size: 10000 })
    if (result.success) {
      templates.value = result.results || []
    }
  } catch (error) {
    console.error('加载模板失败:', error)
  } finally {
    loadingTemplates.value = false
  }
}


// 处理文件上传
const handleFileUpload = (event) => {
  const files = event.target.files
  let addedCount = 0
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    
    // 验证文件大小 (25MB限制)
    if (file.size > 25 * 1024 * 1024) {
      toast.error(`文件 "${file.name}" 超过25MB限制，已跳过`)
      continue
    }
    
    // 检查是否已存在同名文件
    const exists = replyForm.value.attachments.some(existing => existing.name === file.name)
    if (exists) {
      toast.warning(`文件 "${file.name}" 已存在，已跳过`)
      continue
    }
    
    replyForm.value.attachments.push(file)
    addedCount++
  }
  
  if (addedCount > 0) {
    toast.success(`已添加 ${addedCount} 个附件`)
  }
  
  // 清空input，允许重复选择同一文件
  event.target.value = ''
}

// 处理拖拽文件上传
const handleFileDrop = (event) => {
  isDragging.value = false
  const files = event.dataTransfer.files
  
  if (files.length > 0) {
    // 模拟文件选择事件
    const mockEvent = {
      target: { files: files, value: '' }
    }
    handleFileUpload(mockEvent)
  }
}

// 发送邮件
const sendEmail = async (forceSend = false) => {
  // 确保 forceSend 是布尔值
  forceSend = typeof forceSend === 'boolean' ? forceSend : false
  
  if (!isFormValid.value) {
    toast.error('请填写必填字段')
    return
  }
  
  if (sendMode.value === 'scheduled' || isEditMode.value) {
    if (!scheduledAt.value) {
      toast.error('请选择计划发送时间')
      return
    }
    const scheduledDate = new Date(scheduledAt.value)
    if (scheduledDate <= new Date()) {
      toast.error('计划发送时间必须晚于当前时间')
      return
    }
  }
  
  // 如果不是强制发送，先进行敏感词检测
  if (!forceSend) {
    const sensitiveWordResult = await checkSensitiveWords()
    if (sensitiveWordResult) {
      // 检测到敏感词，返回不继续发送
      return
    }
  }
  
  try {
    sending.value = true
    
    // 解析抄送邮箱
    const ccRecipients = replyForm.value.ccEmails.trim()
      ? replyForm.value.ccEmails.split(/[,\s]+/).map(email => {
          const emailTrimmed = email.trim()
          return {
            email: emailTrimmed,
            name: emailTrimmed  // 使用邮箱作为姓名
          }
        }).filter(r => r.email)
      : []
    
    // 准备收件人数据 - 🔧 包含客户ID、品牌独立站表单ID、二维码表单ID用于后端邮箱解析
    const recipientsList = replyForm.value.recipients.filter(r => r.email.trim()).map(r => ({
      email: r.email.trim(),
      name: (r.name || '').trim() || r.email.trim(),
      customer_id: r.customerId || null, // 🔧 传递客户ID给后端
      contact_id: r.contact_id || null, // 🔧 传递品牌独立站联系表单ID
      submission_id: r.submission_id || null // 🔧 传递二维码表单提交ID
    }))
    
    // 构建发送数据 - 后端API要求recipients和cc_recipients为JSON字符串格式
    const sendData = {
      from_account_id: replyForm.value.fromAccountId,
      recipients: JSON.stringify(recipientsList),
      cc_recipients: JSON.stringify(ccRecipients),
      subject: replyForm.value.subject.trim(),
      content: replyForm.value.content.trim(),
      reply_to_email_id: props.isReply ? props.originalEmail?.id : null,
      attachments: replyForm.value.attachments || []
    }
    
    if (sendMode.value === 'scheduled' && !isEditMode.value) {
      sendData.scheduled_at = toISOString(scheduledAt.value)
    }
    
    console.log('📧 准备发送邮件，数据：', {
      ...sendData,
      attachments: sendData.attachments ? sendData.attachments.map(f => ({ name: f.name, size: f.size, type: f.type })) : []
    })
    
    let result
    if (isEditMode.value) {
      result = await emailServiceV2.updateScheduledEmail(props.editReplyLogId, {
        subject: sendData.subject,
        content: sendData.content,
        recipients: sendData.recipients,
        cc_recipients: sendData.cc_recipients,
        scheduled_at: toISOString(scheduledAt.value),
        attachments: sendData.attachments,
        keep_attachment_ids: existingScheduledAttachments.value.map(att => att.id)
      })
    } else {
      result = await emailServiceV2.sendEmail(sendData)
    }
    
    if (!result.success) {
      throw new Error(result.message || result.error || '操作失败')
    }
    
    if (isEditMode.value) {
      toast.success(result.message || '定时邮件已更新')
      emit('updated')
    } else if (result.scheduled) {
      toast.success(result.message || '邮件已加入定时发送计划')
      emit('sent')
    } else {
      toast.success('邮件发送成功')
      emit('sent')
    }
    closeModal()
    
  } catch (error) {
    console.error('发送邮件失败:', error)
    toast.error(`${isEditMode.value ? '保存' : '发送'}失败: ${error.message || error.error}`)
  } finally {
    sending.value = false
  }
}

// 敏感词检测
const checkSensitiveWords = async () => {
  try {
    // 开始检测，显示loading状态
    checkingSensitiveWords.value = true
    
    // 调用敏感词检测API
    const result = await emailTemplateService.checkSensitiveWords({
      subject: replyForm.value.subject.trim(),
      body_html: replyForm.value.content.trim()
    })
    
    if (!result.success) {
      toast.error('敏感词检测失败，请稍后重试')
      return true // 返回 true 阻止发送，而不是 false
    }
    
    const data = result.data
    
    // 如果没有检测到敏感词，可以继续发送
    if (!data.has_sensitive_words) {
      return false
    }
    
    // 如果不能发送（包含禁止级别的敏感词）
    if (!data.can_save) {
      // 只显示禁止级别的敏感词
      const forbiddenWords = data.detected_words.filter(word => word.severity_level === 'forbidden')
      const forbiddenData = {
        ...data,
        detected_words: forbiddenWords
      }
      sensitiveWordErrorData.value = forbiddenData
      showSensitiveWordError.value = true
      return true // 阻止发送
    }
    
    // 如果包含警告级别的敏感词，显示警告模态框
    const warningWords = data.detected_words.filter(word => word.severity_level !== 'forbidden')
    if (warningWords.length > 0) {
      const warningData = {
        ...data,
        detected_words: warningWords
      }
      sensitiveWordData.value = warningData
      showSensitiveWordWarning.value = true
      return true // 等待用户确认
    }
    
    return false // 可以继续发送
  } catch (error) {
    console.error('敏感词检测失败:', error)
    toast.error('敏感词检测失败，请稍后重试')
    return true // 返回 true 阻止发送
  } finally {
    // 无论成功还是失败，都要关闭loading状态
    checkingSensitiveWords.value = false
  }
}

// 处理敏感词警告模态框
const handleSensitiveWordWarningConfirm = async () => {
  // 用户确认继续发送，先关闭警告模态框
  showSensitiveWordWarning.value = false
  sensitiveWordData.value = {}
  
  // 然后强制发送
  await sendEmail(true)
}

const handleSensitiveWordWarningClose = () => {
  showSensitiveWordWarning.value = false
  sensitiveWordData.value = {}
}

// 处理敏感词错误模态框
const handleSensitiveWordErrorClose = () => {
  showSensitiveWordError.value = false
  sensitiveWordErrorData.value = {}
}

// 组件挂载时加载数据
onMounted(() => {
  if (props.showModal) {
    loadAvailableAccounts()
    loadTemplates()
  }
})
</script>

<style scoped>
/* 自定义样式 */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

/* TinyMCE 编辑器自适应高度 */
:deep(.tinymce-editor-wrapper) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.tinymce-editor-wrapper > div) {
  flex: 1;
  min-height: 0;
}

:deep(.tox-tinymce) {
  height: 100% !important;
}
</style>
